import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Trash2 } from "lucide-react";
import { Input } from "../../ui/input";
import { DoctorDto } from "../../../web-api-client";
import { DoctorImage } from "../../doctor/DoctorImage";

type Props = {
  suggestions: DoctorDto[];
  isLoading?: boolean;
  onSearchChange?: (query: string) => void;
  defaultQuota: number;
  selectedDoctors: DoctorDto[];
  onChangeSelected: (doctors: DoctorDto[]) => void;
  quotaByDoctorId: Record<string, number>;
  onChangeQuotaMap: (next: Record<string, number>) => void;
  placeholder?: string;
};

export function DoctorSearchAddTable({
  suggestions,
  isLoading = false,
  onSearchChange,
  defaultQuota,
  selectedDoctors,
  onChangeSelected,
  quotaByDoctorId,
  onChangeQuotaMap,
  placeholder = "Tìm bác sĩ...",
}: Props) {
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const selectedIdSet = useMemo(() => {
    return new Set(
      selectedDoctors.map((d) => d.doctorId).filter(Boolean) as string[],
    );
  }, [selectedDoctors]);

  function addDoctor(d: DoctorDto) {
    if (!d.doctorId) return;

    if (!selectedIdSet.has(d.doctorId)) {
      onChangeSelected([...selectedDoctors, d]);
      onChangeQuotaMap({
        ...quotaByDoctorId,
        [d.doctorId]: quotaByDoctorId[d.doctorId] ?? defaultQuota,
      });
    }

    setQuery("");
    onSearchChange?.("");
    setShowDropdown(false);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function removeDoctor(doctorId: string) {
    onChangeSelected(selectedDoctors.filter((d) => d.doctorId !== doctorId));

    const next = { ...quotaByDoctorId };
    delete next[doctorId];
    onChangeQuotaMap(next);
  }

  function setQuota(doctorId: string, quota: number) {
    const safe = Math.max(1, Math.floor(quota || 1));
    onChangeQuotaMap({ ...quotaByDoctorId, [doctorId]: safe });
  }

  const clearSearch = () => {
    setQuery("");
    onSearchChange?.("");
    setShowDropdown(false);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const el = wrapRef.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) setShowDropdown(false);
    }

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div className="space-y-3" ref={wrapRef}>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
        <Input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            const value = e.target.value;
            setQuery(value);
            onSearchChange?.(value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          className="text-black w-full pl-12 pr-10 rounded border border-gray-300 text-base input-font"
        />

        {query && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-foreground p-1"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {showDropdown && query.trim() && (
          <div className="absolute z-10 top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-card max-h-64 overflow-hidden box-shadow">
            {isLoading ? (
              <div className="flex flex-col bg-white justify-center p-6 items-center">
                <span className="text-gray-400">Đang tìm bác sĩ...</span>
              </div>
            ) : suggestions.length > 0 ? (
              <div className="max-h-64 overflow-auto">
                {suggestions.map((doctor) => (
                  <button
                    type="button"
                    key={doctor.doctorId}
                    onClick={() => addDoctor(doctor)}
                    className="w-full text-left px-4 py-2 hover:bg-muted/30 border-b border-border last:border-b-0"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-9 w-9 bg-green-100 rounded-full overflow-hidden shrink-0">
                        <DoctorImage
                          doctor={doctor}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <span className="text-gray-900 truncate">
                          {(doctor.title ?? "BS") + ". " + (doctor.name ?? "")}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex flex-col bg-white justify-center p-6 items-center">
                <Search className="w-10 h-10 text-gray-300" />
                <span className="text-gray-400 mt-4">
                  Không tìm thấy bác sĩ
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedDoctors.length === 0 ? (
        <p className="rounded-md border border-dashed border-border bg-muted/10 p-4 text-sm text-muted-foreground text-center">
          Chưa có bác sĩ khám.
        </p>
      ) : (
        <div className="rounded-md border border-border bg-white overflow-hidden">
          <div className="grid grid-cols-[1fr_60px_36px] gap-2 px-3 py-2 bg-muted/10 text-xs font-semibold text-muted-foreground">
            <span>Bác sĩ</span>
            <span className="text-center">Hạn mức</span>
            <div />
          </div>

          <div className="divide-y max-h-64 overflow-auto">
            {selectedDoctors.map((d) => {
              const id = d.doctorId!;
              const quota = quotaByDoctorId[id] ?? defaultQuota;

              return (
                <div
                  key={id}
                  className="grid grid-cols-[1fr_60px_36px] gap-2 px-3 py-2 items-center hover:bg-muted/20"
                >
                  <div className="min-w-0 flex gap-2 items-center">
                    <div className="h-9 w-9 bg-green-100 rounded-full overflow-hidden shrink-0">
                      <DoctorImage
                        doctor={d}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="text-sm text-gray-900 line-clamp-2 leading-snug">
                      {d.name ?? ""}
                    </span>
                  </div>

                  <div className="flex justify-center">
                    <Input
                      className="w-[50px] h-8 text-center text-sm px-1 input-font"
                      type="number"
                      min={1}
                      value={quota}
                      onChange={(e) => setQuota(id, Number(e.target.value))}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => removeDoctor(id)}
                    className="h-9 w-9 inline-flex items-center justify-center rounded-full text-red-600 hover:bg-red-50"
                    title="Xóa khỏi khung"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}